'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import ContentfulRichText from '@/components/ContentfulRichText/ContentfulRichText';
import AnchorLink from '@/components/TableOfContents/AnchorLink';
import { v4 as uuidv4 } from 'uuid';
import classNames from 'classnames';
import { analytics } from '@/utils/analytics';
import { DescriptionType } from '@/utils/common.types';
import { submitSubscribeForm } from '@/services/formService';

interface TextProps {
  className: string;
  placeholder: string;
  id: string;
  autocomplete: string;
  minlength?: number;
  maxlength?: number;
  required?: boolean;
}

const CtaTextField = ({
  className,
  placeholder,
  id,
  autocomplete,
  minlength = 2,
  maxlength = 100,
  required = true,
}: TextProps) => {
  return (
    <div className={className}>
      <input
        type="text"
        id={id}
        name={id}
        autoComplete={autocomplete}
        placeholder={placeholder}
        pattern={`[a-zA-Z0-9'-. ]{${minlength},${maxlength}}`}
        minLength={minlength}
        maxLength={maxlength}
        required={required}
        className="relative m-0 h-[3.4375rem] w-full list-none rounded-[4px] border-none bg-white px-[0.8125rem] py-[1.125rem] text-[1.125rem] text-grey-1 shadow-input"
      />
    </div>
  );
};

interface Props {
  title: string;
  description?: DescriptionType;
  includeSegment?: boolean;
}

const CtaCardSubscribe = ({
  title,
  description,
  includeSegment = false,
}: Props) => {
  const [isFormSending, setIsFormSending] = useState(false);
  const [formStatus, setFormStatus] = useState<'success' | 'error' | null>(
    null
  );
  const anchorTagName = 'subscription-form';

  const handleSubmit = async (event) => {
    const formData = new FormData(event.target);
    const formValues = Object.fromEntries(formData);
    setIsFormSending(true);
    await submitSubscribeForm(formValues).then((status) => {
      if (status === 200) {
        setFormStatus('success');
        includeSegment &&
          analytics?.track('Generic Contact Form Submitted', {
            formId: 'subscription-form',
            email: formValues.email,
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            phone: '',
            comments: '',
          });
      } else {
        setFormStatus('error');
      }
    });
    return;
  };

  return (
    <div>
      <div className="h-full rounded-[4px] bg-[url('/public-assets/shape-splat.svg')] bg-cover bg-[50%_0] bg-no-repeat px-0 pb-[100px] pt-2.5">
        <div className="mx-auto my-[50px] w-mobile max-w-standard">
          <AnchorLink anchorTagRef={anchorTagName} />
          <h3 className="mx-0 mb-[18px] mt-0 p-0 text-[40px] font-bold leading-[1.05] text-hbg-blue">
            {title}
          </h3>
          {description && (
            <div className={classNames('desc', 'mx-0 mb-5 mt-3')}>
              {ContentfulRichText(description)}
            </div>
          )}

          {formStatus === 'success' && (
            <div className="pb-[100px] text-green-500">
              We received your request. Thank you.
            </div>
          )}
          {formStatus === 'error' && (
            <div className="pb-[100px] text-red-500">
              Error. Sorry. We could not process your request.
            </div>
          )}
          {!formStatus && (
            <div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit(e);
                }}
                id="subscription-form"
                name="subscribeForm"
              >
                <div className="grid max-w-[900px] grid-cols-1 grid-rows-1 gap-4 lgTablet:grid-cols-4 lgTablet:grid-rows-3">
                  <CtaTextField
                    className="lgTablet:col-start-1 lgTablet:col-end-1 lgTablet:row-start-1 lgTablet:row-end-1"
                    placeholder="First name"
                    id="firstName"
                    autocomplete="given-name"
                  />
                  <CtaTextField
                    className="lgTablet:col-start-2 lgTablet:col-end-2 lgTablet:row-start-1 lgTablet:row-end-1"
                    placeholder="Last name"
                    id="lastName"
                    autocomplete="family-name"
                  />
                  <CtaTextField
                    className="lgTablet:col-start-1 lgTablet:col-end-5 lgTablet:row-start-2 lgTablet:row-end-2"
                    placeholder="Address 1"
                    id="address1"
                    autocomplete="address-line3"
                  />
                  <CtaTextField
                    className="lgTablet:col-start-1 lgTablet:col-end-5 lgTablet:row-start-3 lgTablet:row-end-3"
                    placeholder="Address 2"
                    id="address2"
                    autocomplete="address-line4"
                    required={false}
                  />
                  <CtaTextField
                    className="lgTablet:col-start-1 lgTablet:col-end-3 lgTablet:row-start-4 lgTablet:row-end-4"
                    placeholder="City"
                    id="city"
                    autocomplete="address-line2"
                  />
                  <CtaTextField
                    className="lgTablet:col-start-3 lgTablet:col-end-3 lgTablet:row-start-4 lgTablet:row-end-4"
                    placeholder="State"
                    id="state"
                    autocomplete="address-line1"
                  />
                  <CtaTextField
                    className="lgTablet:col-start-4 lgTablet:col-end-4 lgTablet:row-start-4 lgTablet:row-end-4"
                    placeholder="ZIP Code"
                    id="zip"
                    minlength={5}
                    maxlength={10}
                    autocomplete="postal-code"
                  />
                  <input
                    name="email"
                    type="hidden"
                    value={`${uuidv4()}@example.com`}
                  />
                  <span className="lgTablet:col-start-1 lgTablet:col-end-1 lgTablet:row-start-5 lgTablet:row-end-5">
                    <button
                      type="submit"
                      id="submit-button"
                      disabled={isFormSending}
                      className="cursor-pointer rounded-sm border border-solid bg-hbg-blue px-[30px] py-[17px] text-center font-source-sans text-[14px] font-semibold tracking-[0.5px] text-white disabled:pointer-events-none disabled:opacity-50"
                    >
                      {isFormSending ? 'SENDING . . .' : 'SUBMIT'}
                    </button>
                  </span>
                </div>
              </form>
              <div className="break-words pt-5 font-source-sans text-[18px] text-grey-1">
                We will only use the personal information you have provided to
                mail you a copy of the <i>Opening Doors Magazine</i>. To view
                our privacy policy, please{' '}
                <Link
                  target="_blank"
                  rel="noopener"
                  href="https://www.claytonhomes.com/privacy"
                  className="font-source-sans text-[18px] font-medium leading-[1.69] tracking-normal text-clayton-blue"
                >
                  click here
                </Link>
                .
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CtaCardSubscribe;
